import React, { useState } from "react";
import { Modal, Tabs, Input, Upload, Button, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { addDocumentUrl, uploadDocumentFiles, addDocumentText } from "../../../services/polly/documentService";

const { TextArea } = Input;
const { Dragger } = Upload;

const isYouTube = (url) => /youtube\.com\/watch|youtu\.be\//.test(url);

const MAX_FILE_SIZE_MB = 25;

export default function AddDocumentModal({ visible, onCancel, onSuccess }) {
  const [activeTab, setActiveTab] = useState("url");
  const [submitting, setSubmitting] = useState(false);

  // URL tab state
  const [url, setUrl] = useState("");

  // Upload tab state
  const [fileList, setFileList] = useState([]);

  // Text tab state
  const [textTitle, setTextTitle] = useState("");
  const [textContent, setTextContent] = useState("");

  const resetFields = () => {
    setUrl("");
    setFileList([]);
    setTextTitle("");
    setTextContent("");
    setActiveTab("url");
  };

  const handleCancel = () => {
    resetFields();
    onCancel();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (activeTab === "url") {
        if (!url.trim()) {
          message.error("Please enter a URL");
          setSubmitting(false);
          return;
        }
        await addDocumentUrl(url.trim());
      } else if (activeTab === "upload") {
        if (fileList.length === 0) {
          message.error("Please select at least one file");
          setSubmitting(false);
          return;
        }
        await uploadDocumentFiles(fileList);
      } else if (activeTab === "text") {
        if (!textTitle.trim() || !textContent.trim()) {
          message.error("Please enter both a title and content");
          setSubmitting(false);
          return;
        }
        await addDocumentText(textTitle.trim(), textContent.trim());
      }
      message.success("Document submitted — processing in the background");
      resetFields();
      onSuccess();
    } catch (err) {
      console.warn("Failed to add document:", err);
      message.error("Failed to add document");
    }
    setSubmitting(false);
  };

  const uploadProps = {
    multiple: true,
    accept: ".pdf,.docx,.txt",
    fileList: fileList,
    beforeUpload: (file) => {
      const sizeMB = file.size / 1024 / 1024;
      if (sizeMB > MAX_FILE_SIZE_MB) {
        message.error(`${file.name} exceeds the ${MAX_FILE_SIZE_MB}MB size limit`);
        return Upload.LIST_IGNORE;
      }
      setFileList((prev) => [...prev, file]);
      return false; // prevent auto upload
    },
    onRemove: (file) => {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    },
  };

  const items = [
    {
      key: "url",
      label: "Single Page",
      children: (
        <div>
          <Input
            placeholder="https://example.com/page or YouTube URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <p className="doc-mgmt-upload-hint">
            Paste a web page URL or a YouTube video link.
            {url && isYouTube(url) && " YouTube video detected — transcript will be extracted."}
          </p>
        </div>
      ),
    },
    {
      key: "upload",
      label: "Upload File",
      children: (
        <div>
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag files to upload</p>
            <p className="ant-upload-hint">PDF, DOCX, or TXT — max {MAX_FILE_SIZE_MB}MB per file</p>
          </Dragger>
        </div>
      ),
    },
    {
      key: "text",
      label: "Raw Text",
      children: (
        <div>
          <Input
            placeholder="Document title"
            value={textTitle}
            onChange={(e) => setTextTitle(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <TextArea
            placeholder="Paste or type your content here..."
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            rows={8}
            style={{ minHeight: 160 }}
          />
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="Add Document"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={submitting} onClick={handleSubmit}>
          Add to Vault
        </Button>,
      ]}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} />
    </Modal>
  );
}
